import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SecuritySettingsForm = ({ onPasswordChange, className = '' }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev?.[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.currentPassword) {
      newErrors.currentPassword = 'رمز عبور فعلی الزامی است';
    }

    if (!formData?.newPassword) {
      newErrors.newPassword = 'رمز عبور جدید الزامی است';
    } else if (formData?.newPassword?.length < 8) {
      newErrors.newPassword = 'رمز عبور باید حداقل ۸ کاراکتر باشد';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.newPassword)) {
      newErrors.newPassword = 'رمز عبور باید شامل حروف کوچک، بزرگ و عدد باشد';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'تکرار رمز عبور الزامی است';
    } else if (formData?.newPassword !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'رمز عبور و تکرار آن یکسان نیستند';
    }

    if (formData?.currentPassword === formData?.newPassword) {
      newErrors.newPassword = 'رمز عبور جدید باید با رمز فعلی متفاوت باشد';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock validation for current password
      if (formData?.currentPassword !== 'admin123') {
        setErrors({ currentPassword: 'رمز عبور فعلی اشتباه است' });
        setIsLoading(false);
        return;
      }

      onPasswordChange?.(formData);
      
      // Reset form after successful change
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (error) {
      console.error('Error changing password:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z\d]/?.test(password)) strength++;
    return strength;
  };

  const getStrengthColor = (strength) => {
    if (strength <= 2) return 'bg-error';
    if (strength <= 3) return 'bg-warning';
    return 'bg-success';
  };

  const getStrengthText = (strength) => {
    if (strength <= 2) return 'ضعیف';
    if (strength <= 3) return 'متوسط';
    return 'قوی';
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">
              نکات امنیتی
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• رمز عبور باید حداقل ۸ کاراکتر باشد</li>
              <li>• شامل حروف کوچک، بزرگ و عدد باشد</li>
              <li>• از کاراکترهای خاص استفاده کنید</li>
              <li>• رمز عبور خود را با دیگران به اشتراک نگذارید</li>
            </ul>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="relative">
          <Input
            label="رمز عبور فعلی"
            type={showPasswords?.current ? "text" : "password"}
            value={formData?.currentPassword}
            onChange={(e) => handleInputChange('currentPassword', e?.target?.value)}
            error={errors?.currentPassword}
            required
            placeholder="رمز عبور فعلی خود را وارد کنید"
            description="برای تغییر رمز عبور، ابتدا رمز فعلی را وارد کنید"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('current')}
            className="absolute left-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPasswords?.current ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        <div className="relative">
          <Input
            label="رمز عبور جدید"
            type={showPasswords?.new ? "text" : "password"}
            value={formData?.newPassword}
            onChange={(e) => handleInputChange('newPassword', e?.target?.value)}
            error={errors?.newPassword}
            required
            placeholder="رمز عبور جدید را وارد کنید"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute left-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPasswords?.new ? "EyeOff" : "Eye"} size={16} />
          </button>
          
          {formData?.newPassword && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength(formData?.newPassword))}`}
                    style={{ width: `${(passwordStrength(formData?.newPassword) / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {getStrengthText(passwordStrength(formData?.newPassword))}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label="تکرار رمز عبور جدید"
            type={showPasswords?.confirm ? "text" : "password"}
            value={formData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
            placeholder="رمز عبور جدید را مجدداً وارد کنید"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute left-3 top-9 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPasswords?.confirm ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
          >
            انصراف
          </Button>
          
          <Button
            type="submit"
            loading={isLoading}
            iconName="Shield"
            iconPosition="right"
          >
            تغییر رمز عبور
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SecuritySettingsForm;